import {
  SUBMENU_MOBILE_WIDTH,
  SUBMENU_DESKTOP_WIDTH,
  STANDARD_PADDING,
  STANDARD_MOBILE_PADDING,
} from "./ContextMenu";
import { isMobile } from "react-device-detect";
// Position types
export type SubmenuPosition = "right" | "left" | "overlap";
export type SubmenuSize = "mobile" | "desktop";

// Interfaces for positioning functions
interface RectInfo {
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
  height: number;
}

export interface SpaceInfo {
  spaceRight: number;
  spaceLeft: number;
  spaceBelow: number;
  spaceAbove: number;
  windowWidth: number;
  windowHeight: number;
}

// Generate common style object for submenu based on position type
export const getSubmenuPosition = (
  position: SubmenuPosition,
  rootRect: RectInfo,
  spaceInfo: SpaceInfo,
  size: SubmenuSize = "desktop"
): React.CSSProperties => {
  const submenuWidth = isMobile ? SUBMENU_MOBILE_WIDTH : SUBMENU_DESKTOP_WIDTH;
  const { windowWidth, windowHeight, spaceBelow, spaceAbove } = spaceInfo;

  // Common styles for all positions
  const baseStyle: React.CSSProperties = {
    width: `${submenuWidth}px`,
    overflowY: "auto",
    position: isMobile ? "fixed" : "absolute",
    zIndex: 150,
    right: "auto",
  };

  const getMaxHeight = (pos: SubmenuPosition) => {
    const availableHeight =
      pos === "overlap"
        ? Math.min(windowHeight - STANDARD_PADDING * 2, spaceBelow + spaceAbove)
        : spaceBelow;

    return isMobile
      ? Math.min(windowHeight - 2 * STANDARD_PADDING, windowHeight * 0.7)
      : Math.min(windowHeight * 0.8, availableHeight - STANDARD_PADDING);
  };

  const getMobilePositionStyles = (pos: SubmenuPosition) => {
    const safeLeftOverlap = Math.max(
      STANDARD_MOBILE_PADDING,
      Math.min(
        rootRect.left,
        windowWidth - submenuWidth - STANDARD_MOBILE_PADDING
      )
    );
    const safeTopOverlap = Math.max(
      STANDARD_MOBILE_PADDING,
      Math.min(
        rootRect.top,
        windowHeight - getMaxHeight("overlap") - STANDARD_MOBILE_PADDING
      )
    );

    const mobileStyles: Record<SubmenuPosition, React.CSSProperties> = {
      right: {
        left: `${rootRect.right + 5}px`,
        top: `${Math.max(STANDARD_PADDING, rootRect.top)}px`,
        bottom: "auto",
      },
      left: {
        right: `${windowWidth - rootRect.left + 5}px`,
        left: "auto",
        top: `${Math.max(STANDARD_PADDING, rootRect.top)}px`,
        bottom: "auto",
      },
      overlap: {
        left: `${safeLeftOverlap}px`,
        top: `${safeTopOverlap}px`,
        bottom: "auto",
      },
    };

    return mobileStyles[pos];
  };

  const getDesktopPositionStyles = (pos: SubmenuPosition) => {
    const desktopStyles: Record<SubmenuPosition, React.CSSProperties> = {
      right: {
        left: "100%",
        top: "0",
        bottom: "auto",
      },
      left: {
        left: `-${submenuWidth}px`,
        top: "0",
        bottom: "auto",
        right: "auto",
      },
      overlap: {
        left: "0",
        top: "0",
        bottom: "auto",
        right: "auto",
      },
    };

    return desktopStyles[pos];
  };

  // Create position styles based on device type
  const positionStyles = isMobile
    ? getMobilePositionStyles(position)
    : getDesktopPositionStyles(position);

  // Calculate max height based on position and available space
  const maxHeight = getMaxHeight(position);

  // Final styles with all properties combined
  return {
    ...baseStyle,
    ...positionStyles,
    maxHeight: `${maxHeight}px`,
  };
};

// Helper function to determine optimal position for submenu
export const getOptimalSubmenuPosition = (
  rootRect: RectInfo,
  spaceInfo: SpaceInfo,
  submenuRef: HTMLElement | null,
  size: SubmenuSize = "desktop"
): SubmenuPosition => {
  const submenuWidth = isMobile ? SUBMENU_MOBILE_WIDTH : SUBMENU_DESKTOP_WIDTH;
  const { spaceRight, spaceLeft } = spaceInfo;

  // --- Mobile Logic ---
  if (isMobile) {
    // 1. Prefer Right
    if (spaceRight >= submenuWidth + STANDARD_MOBILE_PADDING) {
      return "right";
    }
    // 2. Then Left
    if (spaceLeft >= submenuWidth + STANDARD_MOBILE_PADDING) {
      return "left";
    }
    // 3. Fallback to Overlap
    return "overlap";
  }

  // --- Desktop Logic ---
  const fitsRightHorizontally =
    rootRect.right + submenuWidth <= spaceInfo.windowWidth;
  const fitsLeftHorizontally = rootRect.left - submenuWidth >= 0;

  // 1. Prefer Right if it fits horizontally
  if (fitsRightHorizontally) {
    return "right";
  }

  // 2. Prefer Left if it fits horizontally
  if (fitsLeftHorizontally) {
    return "left";
  }

  // 3. Fallback to Overlap
  return "overlap";
};

// Get the submenu reference based on type - helper function for external use
export const getSubmenuRefByType = (
  submenuType: string,
  refs: Record<string, HTMLElement | null>
): HTMLElement | null => {
  switch (submenuType) {
    case "worker":
      return refs.workerRef || null;
    case "reason":
      return refs.reasonRef || null;
    case "fix":
      return refs.fixRef || null;
    case "status":
      return refs.statusRef || null;
    case "inspector":
    case "assign-inspector":
      return refs.inspectorRef || null;
    default:
      return null;
  }
};
